// Public signup — tạo shop mới (hoạt động ngay). Link giới thiệu CTV của Đại lý tạo CTV
// thay vì shop — `/auth/referral-info` báo `unitType`, chữ trên trang đổi theo. Link của một Shop
// (referrer.tenantType = 'shop') tạo Shop ngang hàng; `unavailable` = link của Shop đang không dùng được.
// Link giới thiệu của NPP (referrer.tenantType = 'platform', docs/KE_HOACH_BANG_GIA_KHI_TAO_DON_VI.md) tạo được cả Đại lý.
// Đăng ký bằng Số điện thoại + Mật khẩu 5 tiêu chí + Xác nhận Điều khoản sử dụng.
// Khung chung với màn Đăng nhập (components/auth/AuthStage.tsx, styles/login.css).

import { useState, useEffect } from 'react'
import { Link, useNavigate, useSearchParams } from 'react-router-dom'
import { toast } from 'sonner'
import { Eye, EyeOff, Building2, Sparkles, Store, AlertTriangle, LockKeyhole, Mail, UserRound } from 'lucide-react'
import { post, get } from '@/lib/api'
import { Spinner } from '@/components/ui/spinner'
import { AuthStage, AuthTitle } from '@/components/auth/AuthStage'
import { PasswordCriteria } from '@/components/ui/PasswordCriteria'
import { PhoneInput } from '@/components/ui/phone-input'
import { isValidVnPhone, normalizePhoneVN } from '@/lib/format'

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/

export default function SignupPage() {
  const nav = useNavigate()
  const [searchParams] = useSearchParams()
  const refCode = searchParams.get('ref') || undefined
  const initialPhone = searchParams.get('phone') || ''
  const initialShop = searchParams.get('shop') || searchParams.get('name') || ''

  const [shopName, setShopName] = useState(initialShop)
  const [ownerName, setOwnerName] = useState('')
  const [phone, setPhone] = useState(initialPhone)
  const [email, setEmail] = useState(searchParams.get('email') || '')
  const [emailTouched, setEmailTouched] = useState(false)
  const [pw, setPw] = useState('')
  const [showPassword, setShowPassword] = useState(false)
  const [isPasswordValid, setIsPasswordValid] = useState(false)
  const [agreeTerms, setAgreeTerms] = useState(false)
  const [busy, setBusy] = useState(false)
  const [referrer, setReferrer] = useState<{ name: string; tenantType: string } | null>(null)
  const [unitType, setUnitType] = useState<'shop' | 'ctv' | 'agency'>('shop')
  const isShop = unitType === 'shop'
  const unitLabel = unitType === 'agency' ? 'Đại lý' : unitType === 'ctv' ? 'CTV' : 'Shop'
  // Tên NPP sẽ trực thuộc (Sunrise: "ShopTrack"); null = chưa hỏi xong mã giới thiệu — chưa hiện
  // dòng "mạng lưới đối tác …" để NPP khác không thoáng thấy chữ ShopTrack.
  const [refNppName, setRefNppName] = useState<string | null>(null)
  const [refUnavailable, setRefUnavailable] = useState(false)

  useEffect(() => {
    // Giao diện luôn ShopTrack (components/auth/AuthStage.tsx) — chỉ hỏi người giới thiệu + tên NPP sẽ trực thuộc
    if (!refCode) return
    get<{ ok: boolean; unavailable?: boolean; referrer: { name: string; tenantType: string } | null; unitType?: 'shop' | 'ctv' | 'agency'; nppName?: string; nppId?: string }>(
      `/auth/referral-info?ref=${encodeURIComponent(refCode)}`
    )
      .then((res) => {
        if (res.ok && res.referrer) {
          setReferrer(res.referrer)
          setUnitType(res.unitType ?? 'shop')
        }
        setRefUnavailable(!!res.unavailable)
        setRefNppName(res.nppName || 'ShopTrack')
      })
      .catch(() => setRefNppName('ShopTrack'))
  }, [refCode])

  // Kiểm tra tính hợp lệ của SĐT Việt Nam
  const isPhoneValid = isValidVnPhone(phone)
  const isEmailValid = EMAIL_RE.test(email.trim())
  const emailError = emailTouched && email.trim() && !isEmailValid ? 'Email không đúng định dạng' : null

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault()
    if (!agreeTerms) {
      toast.error('Vui lòng tích chọn đồng ý với Điều khoản và điều kiện sử dụng')
      return
    }
    if (!ownerName.trim()) {
      toast.error('Vui lòng nhập họ và tên chủ tài khoản')
      return
    }
    if (!isPhoneValid) {
      toast.error('Số điện thoại không đúng định dạng di động Việt Nam (10 chữ số, đầu 03, 05, 07, 08, 09)')
      return
    }
    if (!isEmailValid) {
      setEmailTouched(true)
      toast.error('Email không đúng định dạng')
      return
    }
    if (!isPasswordValid) {
      toast.error('Mật khẩu chưa đáp ứng đủ 5 điều kiện bảo mật')
      return
    }

    setBusy(true)
    try {
      await post('/auth/signup', {
        shopName: shopName.trim(),
        ownerName: ownerName.trim(),
        phone: normalizePhoneVN(phone),
        email: email.trim().toLowerCase(),
        password: pw,
        agreeTerms: true,
        ref: refCode,
      })
      toast.success(isShop ? 'Đã tạo shop thành công — đăng nhập ngay để bắt đầu' : `Đã tạo tài khoản ${unitLabel} — đăng nhập ngay để bắt đầu`)
      nav('/login', { replace: true })
    } catch (err) {
      toast.error((err as Error).message)
    } finally {
      setBusy(false)
    }
  }

  const canSubmit = Boolean(
    !busy &&
    shopName.trim().length >= 1 &&
    ownerName.trim().length >= 1 &&
    isPhoneValid &&
    isEmailValid &&
    isPasswordValid &&
    agreeTerms
  )

  return (
    <AuthStage homeLink="/">
      <form onSubmit={onSubmit}>
        <AuthTitle>Đăng ký</AuthTitle>

        {referrer ? (
          <div className="lg-ref">
            <span className="lg-ref-icon" aria-hidden>
              {referrer.tenantType === 'agency' || referrer.tenantType === 'platform' ? <Building2 className="size-[18px]" /> : referrer.tenantType === 'shop' ? <Store className="size-[18px]" /> : <Sparkles className="size-[18px]" />}
            </span>
            <span className="min-w-0">
              <span className="lg-ref-label">
                {referrer.tenantType === 'shop'
                  ? 'Đăng ký mở Shop theo lời giới thiệu của shop'
                  : `${isShop ? 'Đăng ký mở Shop' : `Đăng ký làm ${unitLabel}`} theo lời mời từ ${referrer.tenantType === 'agency' ? 'Đại lý phân phối' : referrer.tenantType === 'platform' ? 'Nhà phân phối' : 'Cộng tác viên'}`}
              </span>
              <span className="lg-ref-name">{referrer.name}</span>
            </span>
          </div>
        ) : refCode && refUnavailable ? (
          <div className="lg-ref lg-ref-warn">
            <span className="lg-ref-icon" aria-hidden><AlertTriangle className="size-[18px]" /></span>
            <span className="lg-ref-label">Link giới thiệu không còn dùng được</span>
          </div>
        ) : refCode && refNppName ? (
          <div className="lg-ref lg-ref-soft">
            <span className="lg-ref-icon" aria-hidden><Sparkles className="size-[18px]" /></span>
            <span className="lg-ref-text">Đăng ký tham gia mạng lưới đối tác {refNppName} (Mã: <strong className="font-mono">{refCode}</strong>)</span>
          </div>
        ) : null}

        <div className="lg-field">
          <div className="lg-field-head"><label htmlFor="signup-shop-name">{isShop ? 'Tên shop / Thương hiệu' : `Tên ${unitLabel} / Thương hiệu`}</label></div>
          <div className="lg-input-wrap">
            <Store className="lg-input-icon" aria-hidden />
            <input
              id="signup-shop-name"
              className="lg-input"
              required
              maxLength={200}
              value={shopName}
              onChange={(e) => setShopName(e.target.value)}
              placeholder="Shop Cà Phê Nâu"
              // Chỉ tự focus khi có con trỏ chính xác (chuột). Trên máy cảm ứng,
              // autofocus làm bàn phím ảo bật lên che mất nửa màn hình ngay khi
              // vừa mở trang. Ngưỡng bề rộng cũ (768px) bắt nhầm cả iPad.
              autoFocus={typeof window !== 'undefined' && window.matchMedia('(pointer: fine)').matches}
            />
          </div>
        </div>

        <div className="lg-field">
          <div className="lg-field-head"><label htmlFor="signup-owner-name">Họ và tên chủ tài khoản</label></div>
          <div className="lg-input-wrap">
            <UserRound className="lg-input-icon" aria-hidden />
            <input
              id="signup-owner-name"
              className="lg-input"
              required
              maxLength={100}
              autoComplete="name"
              value={ownerName}
              onChange={(e) => setOwnerName(e.target.value)}
              placeholder="Nguyễn Mai Anh"
            />
          </div>
        </div>

        <div className="lg-field lg-phone">
          <div className="lg-field-head"><label htmlFor="signup-phone">Số điện thoại đăng nhập</label></div>
          <PhoneInput
            id="signup-phone"
            value={phone}
            onValueChange={(val) => setPhone(val || '')}
            showValidation={true}
            required
          />
        </div>

        <div className="lg-field">
          <div className="lg-field-head"><label htmlFor="signup-email">Email</label></div>
          <div className="lg-input-wrap">
            <Mail className="lg-input-icon" aria-hidden />
            <input
              id="signup-email"
              className="lg-input"
              type="email"
              inputMode="email"
              required
              maxLength={254}
              autoComplete="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              onBlur={() => setEmailTouched(true)}
              placeholder="ten@vidu.com"
              aria-invalid={emailError ? true : undefined}
              aria-describedby={emailError ? 'signup-email-hint' : undefined}
            />
          </div>
          {emailError && (
            <p id="signup-email-hint" className="lg-hint" role="alert">
              {emailError}
            </p>
          )}
        </div>

        <div className="lg-field">
          <div className="lg-field-head"><label htmlFor="signup-password">Mật khẩu bảo vệ</label></div>
          <div className="lg-input-wrap">
            <LockKeyhole className="lg-input-icon" aria-hidden />
            <input
              id="signup-password"
              className="lg-input has-eye"
              type={showPassword ? 'text' : 'password'}
              required
              autoComplete="new-password"
              value={pw}
              onChange={(e) => setPw(e.target.value)}
              placeholder="Nhập mật khẩu an toàn"
            />
            <button
              type="button"
              onClick={() => setShowPassword((prev) => !prev)}
              className="lg-eye"
              aria-label={showPassword ? 'Ẩn mật khẩu' : 'Hiện mật khẩu'}
              tabIndex={-1}
            >
              {showPassword ? <EyeOff className="size-[18px]" /> : <Eye className="size-[18px]" />}
            </button>
          </div>

          {/* Checklist 5 điều kiện mật khẩu */}
          <PasswordCriteria
            password={pw}
            onValidChange={setIsPasswordValid}
            className="mt-2.5"
          />
        </div>

        {/* Điều khoản sử dụng */}
        <label htmlFor="agreeTerms" className="lg-check">
          <input
            type="checkbox"
            id="agreeTerms"
            checked={agreeTerms}
            onChange={(e) => setAgreeTerms(e.target.checked)}
          />
          <span>
            Tôi đã đọc, hiểu rõ và đồng ý với{' '}
            <Link to="/terms" target="_blank">Điều khoản và điều kiện sử dụng</Link>{' '}
            và{' '}
            <Link to="/privacy" target="_blank">Chính sách quyền riêng tư</Link>{' '}
            của hệ thống ShopTrack.
          </span>
        </label>

        <button type="submit" className="lg-btn" disabled={!canSubmit} data-busy={busy || undefined}>
          {busy ? <Spinner className="text-white" /> : null}
          {busy ? `Đang khởi tạo ${isShop ? 'shop' : `tài khoản ${unitLabel}`} & cơ sở dữ liệu…` : isShop ? 'Tạo shop miễn phí' : `Tạo tài khoản ${unitLabel}`}
        </button>

        <p className="lg-alt">
          Đã có tài khoản? <Link to="/login">Đăng nhập ngay</Link>
        </p>
      </form>
    </AuthStage>
  )
}
